<template>
    <section class="esesja-modal" @keydown.esc="close">
        <section class="esesja-modal__wraper" role="dialog" aria-modal="true" aria-labelledby="voting-report-settings-title">
            <section class="esesja-modal__wraper__header">
                <h3 id="voting-report-settings-title" class="esesja-modal__wraper__header__title">{{$t('com.file.voting-report-settings-modal.title')}}</h3>
                <button class="esesja-modal__wraper__header__exit-btn"
                        :disabled="isGenerating"
                        @click="(e) => { e.preventDefault(); close(); }">
                    <img src="@/assets/icons/components/exit-btn.svg" />
                </button>
            </section>
            <perfect-scrollbar class="esesja-modal__wraper__body">
                <section class="esesja-modal__row">
                    <label class="esesja-modal__wraper__body__label" for="voting-report-sort-order">{{$t('com.file.voting-report-settings-modal.sort-order')}}</label>
                    <select id="voting-report-sort-order" ref="firstControl" class="esesja-modal__wraper__body__input" v-model="settings.sortOrder" :disabled="isBusy">
                        <option value="agenda">{{$t('com.file.voting-report-settings-modal.sort-agenda')}}</option>
                        <option value="finishedAt">{{$t('com.file.voting-report-settings-modal.sort-finished-at')}}</option>
                    </select>
                </section>

                <section class="esesja-modal__row">
                    <label class="esesja-modal__wraper__body__label" for="voting-report-format">{{$t('com.file.export-email-modal.file-format')}}</label>
                    <select id="voting-report-format" class="esesja-modal__wraper__body__input" v-model="settings.format" :disabled="isBusy">
                        <option value="docx">{{$t('com.file.voting-report-settings-modal.format-docx')}}</option>
                        <option value="pdf">{{$t('com.file.voting-report-settings-modal.format-pdf')}}</option>
                    </select>
                </section>

                <!-- Przełączniki funkcyjne na dole, jak w pozostałych modalach. -->
                <section class="esesja-modal__row">
                    <div class="form-bootstrap-check form-bootstrap-switch voting-report-switch">
                        <input id="voting-report-named-results"
                               class="form-bootstrap-check-input"
                               type="checkbox"
                               role="switch"
                               v-model="settings.includeNamedResults"
                               :disabled="isBusy" />
                        <label class="form-bootstrap-check-label" for="voting-report-named-results">{{$t('com.file.voting-report-settings-modal.named-results')}}</label>
                    </div>
                </section>

                <section class="esesja-modal__wraper__body__bottom">
                    <button class="esesja-modal__wraper__body__bottom-button" :disabled="isGenerating" @click="close">{{$t('com.global.cancel')}}</button>
                    <button class="esesja-modal__wraper__body__bottom-button esesja-modal__wraper__body__bottom-button--red" :disabled="isBusy" @click="generate">{{$t('com.file.voting-report-settings-modal.generate')}}</button>
                </section>
            </perfect-scrollbar>
        </section>
        <LoaderInside v-if="isBusy" />
    </section>
</template>
<script charset="utf-8">
    //components
    import LoaderInside from "@/components/BasicControls/LoaderInside.vue";

    //services
    import VotingService from "@/services/voting.service";
    import UserService from "@/services/user.service";
    import AlertHelper from '@/helpers/alerts.helper.js';

    const SORT_ORDERS = ["agenda", "finishedAt"];
    const FORMATS = ["docx", "pdf"];

    export default {
        name: "VotingReportSettingsModal",
        components: {
            LoaderInside,
        },
        computed: {
            sessionId() {
                return this.$store.state.appStore.votingReportSessionId;
            },
            isBusy() {
                return this.isLoading || this.isGenerating;
            },
        },
        data() {
            return {
                isLoading: false,
                isGenerating: false,
                settings: {
                    includeNamedResults: true,
                    sortOrder: "agenda",
                    format: "docx",
                },
            }
        },
        async mounted() {
            await this.loadSettings();
            this.$nextTick(() => this.$refs.firstControl?.focus());
        },
        methods: {
            async loadSettings() {
                try {
                    this.isLoading = true;
                    // Preferencje są zapisane na koncie (per kadencja); brak wpisu = wartości domyślne z API.
                    const response = await UserService.getUserAccountSettings();
                    const data = response.data || {};

                    this.settings = {
                        includeNamedResults: typeof data.votingReportIncludeNamedResults === "boolean" ? data.votingReportIncludeNamedResults : true,
                        sortOrder: SORT_ORDERS.includes(data.votingReportSortOrder) ? data.votingReportSortOrder : "agenda",
                        format: FORMATS.includes(data.votingReportFormat) ? data.votingReportFormat : "docx",
                    };
                }
                catch {
                    this.showErrorToast('com.file.voting-report-settings-modal.settings-load-error');
                }
                finally {
                    this.isLoading = false;
                }
            },
            async generate() {
                if (this.isBusy)
                    return;

                this.isGenerating = true;
                const options = { ...this.settings };

                try {
                    // Zapis preferencji nie blokuje raportu - plik powstaje także wtedy, gdy zapis się nie uda.
                    const [saveResult, reportResult] = await Promise.allSettled([
                        UserService.saveVotingReportSettings(options),
                        VotingService.getVotingReportBySession(this.sessionId, options),
                    ]);

                    if (reportResult.status !== "fulfilled" || reportResult.value.status !== 200) {
                        // Modal zostaje otwarty z wybranymi opcjami.
                        this.showErrorToast('com.file.voting-report-settings-modal.generate-error');
                        return;
                    }

                    this.downloadFile(reportResult.value);

                    if (saveResult.status !== "fulfilled") {
                        this.showErrorToast('com.file.voting-report-settings-modal.settings-save-error');
                    }

                    this.$store.commit("hideVotingReportSettings");
                }
                finally {
                    this.isGenerating = false;
                }
            },
            close() {
                if (this.isGenerating)
                    return;

                this.$store.commit("hideVotingReportSettings");
            },
            downloadFile(response) {
                const blob = new Blob([response.data], { type: response.headers['content-type'] });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.getFileName(response.headers['content-disposition']);
                link.click();
                setTimeout(() => window.URL.revokeObjectURL(url), 0);
            },
            getFileName(disposition) {
                // filename* niesie polskie znaki; filename to zastępcza wersja ASCII.
                const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition || "");
                if (encoded)
                    return decodeURIComponent(encoded[1]);

                const plain = /filename="?([^";]+)"?/i.exec(disposition || "");
                return plain ? plain[1] : "report";
            },
            showErrorToast(key) {
                const toast = this.$swal.mixin(AlertHelper.errorToastElement());
                toast.fire(AlertHelper.errorToastAlert(this.$t(key), this.$t('com.global.error')));
            },
        },
    }
</script>
<style lang="scss" scoped>
    @use '@/assets/scss/esesja-modal.scss';

    // Bootstrapowy switch cofa kontrolkę o 2.5em liczone od mniejszej czcionki inputa,
    // przez co odstaje od lewej krawędzi etykiet i pól - wyrównujemy do marginesu modala.
    .voting-report-switch {
        padding-left: 0;
        column-gap: 12px;

        .form-bootstrap-check-input {
            margin-left: 0;
            flex-shrink: 0;
        }
    }
</style>
